import { useCallback, useMemo, useState } from "react";

export type PlanName = "Sanctum" | "North Star";
export type BillingCycle = "Monthly" | "Annual";

export type UpgradeSanctumPayload = {
    plan: PlanName;
    billingCycle: BillingCycle;
};

export type UpgradeSanctumProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (payload: UpgradeSanctumPayload) => void;
    /** Optional: surface actual trial end date in the header copy */
    trialEndsOn?: string;
    /** Optional: highlight which plan is currently active for the account */
    currentPlan?: PlanName;
};

import { PLANS } from "../../billing.constants";

export const ALL_FEATURES: string[] = [
    ...PLANS.sanctum.features,
    ...PLANS.northStar.upsellFeatures,
];

type UseUpgradeSanctumArgs = {
    onConfirm: (payload: UpgradeSanctumPayload) => void;
};

export function useUpgradeSanctum({ onConfirm }: UseUpgradeSanctumArgs) {
    // Independent state for plan vs billing cycle
    const [selectedPlan, setSelectedPlan] = useState<PlanName>("North Star");
    const [billingCycle, setBillingCycle] = useState<BillingCycle>("Annual");

    const confirm = useCallback(() => {
        onConfirm({ plan: selectedPlan, billingCycle });
    }, [onConfirm, selectedPlan, billingCycle]);

    const allFeatures = useMemo(() => ALL_FEATURES, []);

    return {
        selectedPlan,
        setSelectedPlan,
        billingCycle,
        setBillingCycle,
        confirm,
        allFeatures,
    };
}

export function getBillingCycleCopy(billingCycle: BillingCycle): string {
    if (billingCycle === "Monthly") {
        return "Your upgrade will be processed immediately. You’ll be charged a prorated amount for the rest of your current billing cycle, and your next payment will reflect the full North Star monthly rate.";
    }

    return ` Your upgrade will be processed immediately. We’ll apply a prorated credit for the
                unused portion of your current Sanctum term, and charge only the difference for the
                remainder of the year. Your billing date will stay the same.`;
}
